import React from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useDayforceBookingsQuery } from 'queries/admin/dayforce/useDayforceBookingsQuery'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useDayforceBookingsFiltersQueryState } from '../hooks/useDayforceBookingsFiltersQueryState'

type DayforceBookingsContextState = {
  state: {
    data: ReturnType<typeof useDayforceBookingsQuery>['data']
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    filters: {
      searchTerm: string
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setFilters: ReturnType<typeof useDayforceBookingsFiltersQueryState>[1]
  }
}

const [Context, useDayforceBookingsContextValue] =
  createContext<DayforceBookingsContextState>({
    name: 'DayforceBookingsContext',
  })

export const DayforceBookingsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [pagination, setPagination] = usePaginationQueryState()
  const [filters, setFilters] = useDayforceBookingsFiltersQueryState()

  const { data, isError, error, dataUpdatedAt, refetch, isFetching } =
    useDayforceBookingsQuery({
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      searchTerm: filters.searchTerm,
    })

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
      filters,
    },
    actions: {
      refetch,
      setPagination,
      setFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useDayforceBookingsContextValue }
